<script lang="ts">
  import type { Template } from '@streepaf/shared'
  import { listsApi } from '../lib/api/lists.js'
  import { i18n, t } from '../lib/i18n/index.svelte.js'
  import { router } from '../lib/router.svelte.js'
  import { myLists } from '../lib/state/myLists.svelte.js'
  import { prefs } from '../lib/state/prefs.svelte.js'
  import { toasts } from '../lib/state/toasts.svelte.js'
  import Button from '../components/ui/Button.svelte'
  import Icon from '../components/ui/Icon.svelte'

  let creating = $state(false)
  let templates = $state<Omit<Template, 'items'>[]>([])

  $effect(() => {
    document.title = 'Streepaf — deel een lijst, streep samen af'
    listsApi
      .templates(i18n.locale)
      .then((result) => (templates = result.templates))
      .catch(() => (templates = []))
  })

  async function create(template?: string): Promise<void> {
    if (creating) return
    creating = true
    try {
      // Name it in the language the person is reading, not the server default.
      const list = await listsApi.create(template ? { template } : { title: t('unnamed') })
      myLists.remember({
        token: list.tokens.edit ?? list.tokens.view,
        role: 'admin',
        title: list.title,
        emoji: list.emoji,
        total: list.stats.total,
        checked: list.stats.checked,
      })
      router.navigate(`/e/${list.tokens.edit}?new=1`)
    } catch {
      toasts.error(t('error_title'))
    } finally {
      creating = false
    }
  }

  function href(entry: { token: string; role: string }): string {
    const prefix = entry.role === 'view' ? 'v' : entry.role === 'admin' ? 'a' : 'e'
    return `/${prefix}/${entry.token}`
  }
</script>

<main class="page">
  <section class="hero">
    <div class="brand"><Icon name="check" size={22} /> Streepaf</div>
    <h1>{t('home_title')}</h1>
    <p class="sub">{t('home_sub')}</p>
    <Button variant="primary" size="lg" full disabled={creating} onclick={() => create()}>
      {creating ? t('home_new_busy') : t('home_new')}
    </Button>
  </section>

  {#if myLists.entries.length > 0}
    <section>
      <h2>{t('home_your_lists')}</h2>
      <ul class="cards">
        {#each myLists.entries.slice(0, 12) as entry (entry.token)}
          <li class="card" class:missing={entry.missing}>
            <a href={href(entry)}>
              <span class="card-emoji">{entry.emoji ?? '📝'}</span>
              <span class="card-body">
                <span class="card-title">{entry.title || t('unnamed')}</span>
                <span class="card-meta">
                  {#if entry.total > 0}<span>{entry.checked}/{entry.total}</span>{/if}
                  <span class="pill">{entry.role === 'view' ? t('role_view') : t('role_edit')}</span>
                  <span>{i18n.relative(entry.lastVisited)}</span>
                </span>
              </span>
            </a>
            <button
              class="forget"
              aria-label={t('remove_from_device')}
              title={t('remove_from_device')}
              onclick={() => myLists.forget(entry.token)}
            >
              <Icon name="close" size={16} />
            </button>
          </li>
        {/each}
      </ul>
      <p class="hint">{t('home_your_lists_hint')}</p>
    </section>
  {/if}

  {#if templates.length > 0}
    <section>
      <h2>{t('home_templates')}</h2>
      <ul class="templates">
        {#each templates as template (template.id)}
          <li>
            <button onclick={() => create(template.id)} disabled={creating}>
              <span class="template-emoji">{template.emoji}</span>
              <span class="template-title">{template.title}</span>
              <span class="template-count">{template.itemCount}</span>
            </button>
          </li>
        {/each}
      </ul>
    </section>
  {/if}

  <section>
    <h2>{t('home_how')}</h2>
    <ol class="steps">
      <li>
        <strong>{t('home_step_1_title')}</strong>
        <span>{t('home_step_1_body')}</span>
      </li>
      <li>
        <strong>{t('home_step_2_title')}</strong>
        <span>{t('home_step_2_body')}</span>
      </li>
      <li>
        <strong>{t('home_step_3_title')}</strong>
        <span>{t('home_step_3_body')}</span>
      </li>
    </ol>
    <h2>{t('home_privacy_title')}</h2>
    <p class="prose">{t('home_privacy_body')}</p>
    <p class="prose">
      <a href="/llms.txt" target="_blank" rel="noopener" data-native>{t('home_devs')}</a>
    </p>
  </section>

  <footer>
    <button onclick={() => i18n.toggle()}>
      <Icon name="globe" size={16} />
      {i18n.locale === 'nl' ? 'English' : 'Nederlands'}
    </button>
    <button
      onclick={() =>
        prefs.setTheme(prefs.theme === 'dark' ? 'light' : prefs.theme === 'light' ? 'auto' : 'dark')}
    >
      <Icon name="sun" size={16} />
      {prefs.theme === 'auto'
        ? t('menu_theme_auto')
        : prefs.theme === 'light'
          ? t('menu_theme_light')
          : t('menu_theme_dark')}
    </button>
    <a href="/docs" target="_blank" rel="noopener" data-native>API</a>
  </footer>
</main>

<style>
  .hero {
    padding: var(--space-6) 0 var(--space-5);
  }
  .brand {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    margin-bottom: var(--space-5);
    font-weight: 700;
    color: var(--accent);
  }
  h1 {
    font-size: clamp(28px, 7vw, 38px);
    line-height: 1.15;
    letter-spacing: -0.02em;
    margin-bottom: var(--space-3);
  }
  .sub {
    margin-bottom: var(--space-5);
    font-size: 17px;
    color: var(--fg-muted);
  }
  section {
    margin-bottom: var(--space-6);
  }
  h2 {
    margin-bottom: var(--space-3);
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--fg-muted);
  }
  .cards {
    list-style: none;
    display: grid;
    gap: var(--space-2);
    margin: 0;
    padding: 0;
  }
  .card {
    position: relative;
    display: flex;
    align-items: center;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    background: var(--surface);
    transition: border-color var(--dur-fast) var(--ease);
  }
  .card:hover {
    border-color: var(--border-strong);
  }
  .card.missing {
    opacity: 0.55;
  }
  .card a {
    display: flex;
    flex: 1;
    align-items: center;
    gap: var(--space-3);
    min-width: 0;
    padding: var(--space-3);
    color: inherit;
    text-decoration: none;
  }
  .card-emoji {
    font-size: 24px;
    line-height: 1;
  }
  .card-body {
    min-width: 0;
  }
  .card-title {
    display: block;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .card-meta {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
    margin-top: 2px;
    font-size: 13px;
    color: var(--fg-muted);
  }
  .pill {
    padding: 0 var(--space-2);
    border-radius: 999px;
    background: var(--surface-2);
  }
  .forget {
    display: grid;
    place-items: center;
    width: 44px;
    height: 44px;
    border: none;
    background: transparent;
    color: var(--fg-faint);
  }
  .forget:hover {
    color: var(--fg);
  }
  .hint {
    margin-top: var(--space-2);
    font-size: 13px;
    color: var(--fg-faint);
  }
  .templates {
    list-style: none;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: var(--space-2);
    margin: 0;
    padding: 0;
  }
  .templates button {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    width: 100%;
    min-height: 56px;
    padding: 0 var(--space-3);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    background: var(--surface);
    text-align: left;
  }
  .templates button:hover:not(:disabled) {
    border-color: var(--accent);
  }
  .template-emoji {
    font-size: 20px;
  }
  .template-title {
    flex: 1;
    font-weight: 550;
  }
  .template-count {
    font-size: 13px;
    color: var(--fg-faint);
  }
  .steps {
    list-style: none;
    display: grid;
    gap: var(--space-3);
    margin: 0 0 var(--space-5);
    padding: 0;
    counter-reset: step;
  }
  .steps li {
    display: grid;
    gap: 2px;
  }
  .steps strong::before {
    counter-increment: step;
    content: counter(step) '. ';
    color: var(--accent);
  }
  .steps span,
  .prose {
    color: var(--fg-muted);
  }
  .prose {
    margin-bottom: var(--space-3);
  }
  footer {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-3);
    padding-top: var(--space-4);
    border-top: 1px solid var(--border);
    font-size: 14px;
  }
  footer button {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    min-height: 40px;
    padding: 0 var(--space-2);
    border: none;
    background: transparent;
    color: var(--fg-muted);
  }
  footer button:hover {
    color: var(--fg);
  }
  footer a {
    color: var(--fg-muted);
  }
</style>
